/**
 * Formatul propriu de export: un fișier JSON cu un antet în clar (format,
 * versiune, parametri KDF, salt, nonce) și conținutul criptat cu o cheie
 * derivată din parola de export.
 *
 *   {
 *     "format": "cerbes-export", "version": 1,
 *     "kdf": { "algorithm": "argon2id", "opslimit": 3, "memlimit": 67108864, "salt": "<b64>" },
 *     "cipher": "secretbox", "nonce": "<b64>", "ciphertext": "<b64>"
 *   }
 *
 * Conținutul decriptat:
 *   { "version": 1, "exported_at": "<ISO>", "credentials": [TransferCredential, ...] }
 *
 * Antetul nu e autentificat (secretbox nu are date asociate), dar nici nu
 * trebuie: orice modificare a lui duce la altă cheie sau alt nonce, deci la o
 * decriptare eșuată.
 */
import type { MetaValue } from '@/api/credentials';
import {
  base64ToBytes,
  bytesToBase64,
  bytesToStr,
  decryptWithPassword,
  DEFAULT_KDF_PARAMS,
  encryptWithPassword,
  strToBytes,
} from '@/crypto/sodium';
import {
  CREDENTIAL_TYPES,
  isRecord,
  TransferError,
  type TransferCredential,
} from '@/features/vault/transfer/types';

export const EXPORT_FORMAT = 'cerbes-export';
export const EXPORT_VERSION = 1;
export const EXPORT_FILE_SUFFIX = '.cerbes-export.json';

const SALT_BYTES = 16;
const NONCE_BYTES = 24;

// Parametrii KDF vin din fișier, deci dintr-o sursă nesigură: fără plafon, un
// fișier pregătit special ar cere gigabytes de memorie și ar bloca tab-ul.
const KDF_LIMITS = {
  opslimit: { min: 1, max: 16 },
  memlimit: { min: 8 * 1024 * 1024, max: 1024 * 1024 * 1024 },
};

export interface ExportContent {
  exported_at: string;
  credentials: TransferCredential[];
}

/** Serializează și criptează credentialele cu parola de export. */
export async function encodeExport(
  credentials: TransferCredential[],
  password: string,
  kdf: { opslimit: number; memlimit: number } = DEFAULT_KDF_PARAMS,
  now: Date = new Date(),
): Promise<string> {
  const plaintext = strToBytes(
    JSON.stringify({
      version: EXPORT_VERSION,
      exported_at: now.toISOString(),
      credentials,
    }),
  );
  try {
    const sealed = await encryptWithPassword(plaintext, password, kdf);
    return JSON.stringify(
      {
        format: EXPORT_FORMAT,
        version: EXPORT_VERSION,
        kdf: {
          algorithm: 'argon2id',
          opslimit: sealed.opslimit,
          memlimit: sealed.memlimit,
          salt: bytesToBase64(sealed.salt),
        },
        cipher: 'secretbox',
        nonce: bytesToBase64(sealed.nonce),
        ciphertext: bytesToBase64(sealed.ciphertext),
      },
      null,
      2,
    );
  } finally {
    // Copia în clar a întregului vault nu rămâne în memorie după criptare.
    plaintext.fill(0);
  }
}

function inRange(value: unknown, limits: { min: number; max: number }): value is number {
  return Number.isInteger(value) && (value as number) >= limits.min && (value as number) <= limits.max;
}

function decodeBase64(value: unknown, exactLength?: number): Uint8Array {
  if (typeof value !== 'string') throw new TransferError('invalid_file');
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(value);
  } catch {
    throw new TransferError('invalid_file');
  }
  if (exactLength !== undefined && bytes.length !== exactLength) {
    throw new TransferError('invalid_file');
  }
  return bytes;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function isMetaValue(value: unknown): value is MetaValue {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

/** Validează o intrare din fișier; orice abatere de la formă invalidează fișierul. */
function toCredential(value: unknown): TransferCredential {
  if (!isRecord(value)) throw new TransferError('invalid_file');
  const type = CREDENTIAL_TYPES.find((t) => t === value.type);
  if (!type || typeof value.title !== 'string') throw new TransferError('invalid_file');
  const meta: Record<string, MetaValue> = {};
  if (isRecord(value.meta)) {
    for (const [key, v] of Object.entries(value.meta)) {
      if (isMetaValue(v)) meta[key] = v;
    }
  }
  return {
    type,
    title: value.title,
    url: nullableString(value.url),
    username: nullableString(value.username),
    expires_at: nullableString(value.expires_at),
    meta,
    payload: isRecord(value.payload) ? value.payload : {},
  };
}

/**
 * Decriptează și validează un fișier de export. Aruncă `TransferError`:
 * `invalid_file` (nu e un export Cerbes sau e deteriorat), `unsupported_version`
 * sau `wrong_password`.
 */
export async function decodeExport(raw: string, password: string): Promise<ExportContent> {
  let file: unknown;
  try {
    file = JSON.parse(raw);
  } catch {
    throw new TransferError('invalid_file');
  }
  if (!isRecord(file) || file.format !== EXPORT_FORMAT) throw new TransferError('invalid_file');
  if (file.version !== EXPORT_VERSION) throw new TransferError('unsupported_version');
  const kdf = file.kdf;
  if (
    !isRecord(kdf) ||
    kdf.algorithm !== 'argon2id' ||
    file.cipher !== 'secretbox' ||
    !inRange(kdf.opslimit, KDF_LIMITS.opslimit) ||
    !inRange(kdf.memlimit, KDF_LIMITS.memlimit)
  ) {
    throw new TransferError('invalid_file');
  }
  const blob = {
    salt: decodeBase64(kdf.salt, SALT_BYTES),
    nonce: decodeBase64(file.nonce, NONCE_BYTES),
    ciphertext: decodeBase64(file.ciphertext),
    opslimit: kdf.opslimit,
    memlimit: kdf.memlimit,
  };

  let plaintext: Uint8Array;
  try {
    plaintext = await decryptWithPassword(blob, password);
  } catch {
    // Parolă greșită și conținut modificat arată la fel pentru secretbox.
    throw new TransferError('wrong_password');
  }
  try {
    let content: unknown;
    try {
      content = JSON.parse(bytesToStr(plaintext));
    } catch {
      throw new TransferError('invalid_file');
    }
    if (!isRecord(content) || !Array.isArray(content.credentials)) {
      throw new TransferError('invalid_file');
    }
    return {
      exported_at: typeof content.exported_at === 'string' ? content.exported_at : '',
      credentials: content.credentials.map(toCredential),
    };
  } finally {
    plaintext.fill(0);
  }
}
